(() => {
  if (document.documentElement.hasAttribute('data-vx-motion-ready')) return;
  document.documentElement.setAttribute('data-vx-motion-ready', '');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover:hover) and (pointer:fine)');
  const bound = new WeakSet();
  const running = new Set();
  const perElement = new WeakMap();
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const play = (element, frames, options = {}) => {
    if (!element?.animate || reduced.matches) return;
    perElement.get(element)?.cancel();
    const animation = element.animate(frames, {duration:320, easing:ease, ...options});
    perElement.set(element, animation);
    running.add(animation);
    animation.finished.catch(() => {}).finally(() => {
      running.delete(animation);
      if (perElement.get(element) === animation) perElement.delete(element);
    });
  };
  const bindDetails = detail => {
    if (bound.has(detail)) return;
    bound.add(detail);
    const picker = detail.matches('.vtx-hero-project-picker');
    const summary = detail.querySelector('summary');
    const panel = picker ? detail.querySelector('nav') : detail.querySelector(':scope > .vxa-accordion__content, :scope > div');
    let leaveTimer;
    detail.addEventListener('toggle', () => {
      if (!detail.open) {
        if (panel) perElement.get(panel)?.cancel();
        return;
      }
      play(panel, picker ? [
        {opacity:0,transform:'translateY(-8px) scale(.985)',clipPath:'inset(0 0 16% 0 round 4px)'},
        {opacity:1,transform:'translateY(0) scale(1)',clipPath:'inset(0 0 0 0 round 4px)'}
      ] : [{opacity:.3,transform:'translateY(-5px)'},{opacity:1,transform:'translateY(0)'}], {duration:picker ? 360 : 220});
    });
    if (!picker) return;
    detail.addEventListener('pointerenter', event => {
      clearTimeout(leaveTimer);
      if (finePointer.matches && event.pointerType !== 'touch') detail.open = true;
    });
    detail.addEventListener('pointerleave', () => {
      if (!finePointer.matches) return;
      leaveTimer = setTimeout(() => {
        if (!detail.contains(document.activeElement)) detail.open = false;
      }, 200);
    });
    detail.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!detail.contains(document.activeElement) && !detail.matches(':hover')) detail.open = false;
      });
    });
    detail.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      detail.open = false;
      summary?.focus();
    });
    document.addEventListener('click', event => {
      if (detail.isConnected && !detail.contains(event.target)) detail.open = false;
    });
  };
  const bindLayer = layer => {
    if (bound.has(layer)) return;
    bound.add(layer);
    const panel = layer.querySelector('.vtx-search-form,.vtx-project-modal__panel,.vtx-finder-modal__panel,.vtx-global-finder__panel');
    let wasOpen = !layer.hidden;
    const observer = new MutationObserver(() => {
      const open = !layer.hidden;
      if (open && !wasOpen) play(panel, [{opacity:.2,transform:'translateY(16px) scale(.985)'},{opacity:1,transform:'translateY(0) scale(1)'}], {duration:380});
      if (!open && panel) perElement.get(panel)?.cancel();
      wasOpen = open;
    });
    observer.observe(layer, {attributes:true,attributeFilter:['hidden']});
    layer.addEventListener('vx:dispose', () => observer.disconnect(), {once:true});
  };
  const init = (root = document) => {
    root.querySelectorAll('.vtx-hero-project-picker,.vxa-accordion,.vx-pdp__faq-list details,.vxb2b .faq details,.vxkit .faq details').forEach(bindDetails);
    root.querySelectorAll('.vtx-search-modal,.vtx-project-modal,.vtx-finder-modal,.vtx-global-finder').forEach(bindLayer);
  };
  let activeCard = null;
  let cardRect = null;
  let pointerFrame = 0;
  let pointerX = 0;
  let pointerY = 0;
  const clearCard = () => {
    cancelAnimationFrame(pointerFrame);
    if (activeCard) {
      activeCard.style.removeProperty('--vx-image-x');
      activeCard.style.removeProperty('--vx-image-y');
    }
    activeCard = null;
    cardRect = null;
  };
  document.addEventListener('pointerover', event => {
    if (reduced.matches || !finePointer.matches || event.pointerType === 'touch') return;
    const card = event.target.closest?.('.vtx-product-card');
    if (!card || card === activeCard) return;
    clearCard();
    activeCard = card;
    cardRect = card.getBoundingClientRect();
  }, {passive:true});
  document.addEventListener('pointermove', event => {
    if (!activeCard || !cardRect || reduced.matches || !finePointer.matches) return;
    pointerX = event.clientX;
    pointerY = event.clientY;
    if (pointerFrame) return;
    pointerFrame = requestAnimationFrame(() => {
      pointerFrame = 0;
      if (!activeCard || !cardRect) return;
      const x = Math.max(-1, Math.min(1, (pointerX - cardRect.left) / cardRect.width * 2 - 1));
      const y = Math.max(-1, Math.min(1, (pointerY - cardRect.top) / cardRect.height * 2 - 1));
      activeCard.style.setProperty('--vx-image-x', `${(-y * 2).toFixed(2)}deg`);
      activeCard.style.setProperty('--vx-image-y', `${(x * 2).toFixed(2)}deg`);
    });
  }, {passive:true});
  document.addEventListener('pointerout', event => {
    if (activeCard && !activeCard.contains(event.relatedTarget)) clearCard();
  }, {passive:true});
  window.addEventListener('blur', clearCard);
  window.addEventListener('scroll', clearCard, {passive:true});
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearCard();
      running.forEach(animation => animation.cancel());
    }
  });
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      clearCard();
      running.forEach(animation => animation.cancel());
    }
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.vtx-search-modal,.vtx-project-modal,.vtx-finder-modal,.vtx-global-finder').forEach(layer => layer.dispatchEvent(new Event('vx:dispose')));
    clearCard();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();
